// Joukkueet (Teams / sarjaottelut). docs/features/teams.md §4.3.
//
// KEEP THIS FILE FREE OF REACT AND FIREBASE, for the same reason as utils/teamformats.js:
// managerIdsOf() and teamQuota() are read by the Phase E backend as well as by this repo's
// admin UI. A quota the two sides compute differently is a manager being allowed to create a
// tie the server then refuses, or worse, one it should have refused.

const isPositive = (n) => typeof n === 'number' && isFinite(n) && n > 0

// Quota is DERIVED, never stored: home ties, plus the one spare the manager cancels once
// enough opponents have confirmed, plus whatever the admin granted on top. Storing the total
// alongside `homematches` would be two sources of truth that drift the first time someone
// edits the one number the admin actually types.
//
// A team whose `homematches` is missing or non-positive is half-typed config, not a team with
// one spare tie, so it gets no quota at all rather than the bare +1. Same reasoning as
// validRows() in teamformats.js: never let unfinished config read as a bookable allowance.
export const teamQuota = (team) => {
  if (!team || !isPositive(team.homematches)) return 0
  const extra = isPositive(team.quotaextra) ? team.quotaextra : 0
  return team.homematches + 1 + extra
}

// The flat id mirror of `managers`. Firestore cannot array-contains on a field inside an array
// of objects, so the rich array is unqueryable and the enduser app could not answer "which
// teams does this user manage" without it (§4.3).
//
// This exists as a function, and firebase.js derives the field through it on every write,
// precisely so the two can never be edited apart. Never assemble managerids at a call site.
export const managerIdsOf = (managers) => {
  const list = Array.isArray(managers) ? managers : []
  const ids = list.filter((m) => m && m.id).map((m) => m.id)
  return ids.filter((id, i) => ids.indexOf(id) === i)
}

// Narrows a person search hit down to what a team stores about its manager. The search returns
// an Algolia record with far more on it; copying the lot would silently snapshot a person's
// details into every team they manage and leave them stale the day they change their email.
// These five fields are the ones a venue needs to phone a manager without a join.
export const managerEntry = (person) => ({
  id: person.id,
  firstname: person.firstname || '',
  lastname: person.lastname || '',
  email: person.email || '',
  phone: person.phone || '',
})

export const managerName = (manager) => {
  if (!manager) return ''
  const name = [manager.firstname, manager.lastname].filter((p) => p).join(' ')
  return name || manager.email || ''
}

// Carries the email, not just the name. A venue can hold two Matti Virtanens, and a list that
// prints only the name cannot show an admin that booking rights went to the wrong one - the
// same reason the editor shows the email on the picked manager card.
export const describeManagers = (managers) => {
  const list = Array.isArray(managers) ? managers : []
  return list
    .map((m) => {
      const name = managerName(m)
      if (!name) return ''
      // managerName already falls back to the email, so repeating it would read "a@b (a@b)".
      return m && m.email && m.email !== name ? name + ' (' + m.email + ')' : name
    })
    .filter((n) => n)
    .join(', ')
}

// Persons and organisations come out of the same combined search and are told apart by
// `businessId` — the discriminator PersonAndOrganisationSearch itself uses when it labels a
// chip (PersonAndOrganisationSearch.jsx:122, :154).
export const isOrganisationEntity = (entity) => !!(entity && entity.businessId)

export const entityName = (entity) => {
  if (!entity) return ''
  if (isOrganisationEntity(entity)) return entity.name || ''
  return managerName(entity)
}

// The lines shown under the name on a picked-entity card, in the order a venue reads them to
// answer "is this the right one?". Persons and organisations share the card, so this handles
// both: an organisation leads with its business id, which is the only thing that separates two
// clubs registered under the same trading name.
//
// The email's ABSENCE is stated rather than left blank. An admin comparing two same-named
// people needs to know this one has no email at all - both because that is itself the
// distinguishing fact, and because a manager with no email cannot be sent their booking link
// (§6). A silently missing line reads as a rendering fault instead.
export const entityDetails = (entity) => {
  if (!entity) return []
  const lines = []
  if (isOrganisationEntity(entity) && entity.businessId) lines.push(entity.businessId)
  lines.push(entity.email || '(ei sähköpostia)')
  if (entity.phone) lines.push(entity.phone)
  return lines
}

// What the venue is told about who pays, for one team. Deliberately a LABEL and not a
// resolution: `payer` on the team is an entity, while `payerdefault` on the season is a mode
// ('team' | 'manager' | 'organisation'), and turning the mode into an actual payer at booking
// time is the backend's job (§4.1, and open question 6 — the season-level default may yet turn
// out to be dead config). Resolving it here as well would put a second, quieter answer in the
// admin UI for the venue to be misled by.
export const PAYER_MODES = [
  { const: 'team', title: 'Joukkueelle merkitty maksaja' },
  { const: 'manager', title: 'Joukkueenjohtaja' },
  { const: 'organisation', title: 'Kiinteä yritys / seura' },
]

export const payerModeLabel = (mode) => {
  const found = PAYER_MODES.find((m) => m.const === mode)
  return found ? found.title : ''
}

export const payerLabel = (team, season) => {
  if (team && team.payer) return entityName(team.payer)
  const mode = season && season.payerdefault
  const label = payerModeLabel(mode)
  return label ? 'Kaudelta: ' + label : ''
}

export const TEAM_STATES = [
  { const: 'active', title: 'Käytössä' },
  { const: 'paused', title: 'Tauolla' },
]

export const teamStateLabel = (state) => {
  const found = TEAM_STATES.find((s) => s.const === state)
  return found ? found.title : state || ''
}
